import { findLocationsNearby as findLocationsNearbyOp } from '@gql/operations'
import { calculateDistanceMeters, type GqlExecutor } from '@hopdrive/sdk-core'
import type { Location } from '../../types'
import { DEFAULT_MATCHING_CONFIG } from '../../utils/scoring'

export interface FindNearbyParams {
  customerId: number
  lat: number
  lng: number
  radiusMeters?: number
  executor?: GqlExecutor
}

export type LocationWithDistance = Location & { distance_meters: number }

/**
 * Find locations within proximity radius using PostGIS ST_DWithin.
 *
 * Uses Hasura's _st_d_within operator on the geopoint geography column.
 * Distance is calculated client-side using the Haversine formula.
 */
export async function findLocationsNearby(params: FindNearbyParams): Promise<LocationWithDistance[]> {
  const radiusMeters = params.radiusMeters ?? DEFAULT_MATCHING_CONFIG.PROXIMITY_RADIUS_METERS

  // Build GeoJSON Point for the _st_d_within query
  const point = {
    type: 'Point',
    coordinates: [params.lng, params.lat], // GeoJSON uses [longitude, latitude]
  }

  const result = await findLocationsNearbyOp(
    {
      customerId: params.customerId,
      point,
      radiusMeters,
    },
    { executor: params.executor }
  )

  const locations = (result ?? []) as Location[]

  // Calculate distance for each location using Haversine formula
  return locations.map(location => ({
    ...location,
    distance_meters: calculateDistanceMeters(
      params.lat,
      params.lng,
      location.latitude ?? 0,
      location.longitude ?? 0
    ),
  }))
}

